/**
 * 公开接口 API 服务
 * 无需登录、无需加密
 */

import type {
  Announcement,
  Banner,
  CustomerServiceLinks,
  LanguageItem,
  ProviderInfo,
  SectionInfo,
  GameTypeInfo,
  PaginatedResponse,
} from '@/types/api'
import { get } from '@/services/request'

// code → 展示名 (与 LanguagePage 兜底列表一致)
const LANGUAGE_NAMES: Record<string, string> = {
  zh: '简体中文',
  'zh-tw': '繁體中文',
  en: 'English',
  ko: '한국어',
  vi: 'Tiếng Việt',
  th: 'ไทย',
  ja: '日本語',
  id: 'Bahasa Indonesia',
  ms: 'Bahasa Melayu',
  pt: 'Português',
}

/**
 * 解析后端语言 value 字段
 * swagger 标注为 JSON 数组串 '["zh","en"]', 但测试后端实际返回逗号分隔串 'en,zh,th,vi'
 * → 两种格式都兼容, 统一只取 code
 */
function parseLanguageCodes(value: string): string[] {
  const raw = value.trim()
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.map(v => (typeof v === 'string' ? v : v?.code)).filter(Boolean)
    }
  } catch {
    // 非 JSON, 落到下面 CSV 分支
  }
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

/**
 * 健康检查
 */
export async function healthCheck(): Promise<{
  success: boolean
  status?: string
  error?: string
}> {
  try {
    const response = await get<{ status: string }>('/health')
    return { success: true, status: response.data?.status }
  } catch (error) {
    console.error('Health check error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取公告列表
 */
export async function getAnnouncements(): Promise<{
  success: boolean
  data?: PaginatedResponse<Announcement>
  error?: string
}> {
  try {
    const response = await get<PaginatedResponse<Announcement>>('/api/v1/public/announcements')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取公告失败' }
  } catch (error) {
    console.error('Get announcements error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取轮播图列表
 */
export async function getBanners(): Promise<{
  success: boolean
  data?: PaginatedResponse<Banner>
  error?: string
}> {
  try {
    const response = await get<PaginatedResponse<Banner>>('/api/v1/public/banners')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取轮播图失败' }
  } catch (error) {
    console.error('Get banners error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取启用语言列表
 */
export async function getLanguages(): Promise<{
  success: boolean
  data?: LanguageItem[]
  error?: string
}> {
  try {
    const response = await get<{ value: string }>('/api/v1/public/languages')

    if (response.code === 0 && response.data?.value) {
      const languages: LanguageItem[] = parseLanguageCodes(response.data.value).map((code, i) => ({
        code,
        name: LANGUAGE_NAMES[code] ?? code.toUpperCase(),
        sort: i + 1,
      }))
      return { success: true, data: languages }
    }

    return { success: false, error: response.msg || '获取语言列表失败' }
  } catch (error) {
    console.error('Get languages error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取客服链接
 */
export async function getCustomerServiceLinks(): Promise<{
  success: boolean
  data?: CustomerServiceLinks
  error?: string
}> {
  try {
    const response = await get<CustomerServiceLinks>('/api/v1/public/customer-service')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取客服链接失败' }
  } catch (error) {
    console.error('Get customer service links error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取公开厂牌列表
 */
export async function getProviders(): Promise<{
  success: boolean
  data?: ProviderInfo[]
  error?: string
}> {
  try {
    const response = await get<{ items: ProviderInfo[] }>('/api/v1/public/providers')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items }
    }

    return { success: false, error: response.msg || '获取厂牌列表失败' }
  } catch (error) {
    console.error('Get providers error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取板块列表（含游戏）
 */
export async function getSections(): Promise<{
  success: boolean
  data?: SectionInfo[]
  error?: string
}> {
  try {
    const response = await get<{ items: SectionInfo[] }>('/api/v1/public/sections')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items }
    }

    return { success: false, error: response.msg || '获取板块列表失败' }
  } catch (error) {
    console.error('Get sections error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 获取游戏类型列表
 */
export async function getGameTypes(): Promise<{
  success: boolean
  data?: GameTypeInfo[]
  error?: string
}> {
  try {
    const response = await get<{ items: GameTypeInfo[] }>('/api/v1/public/game-types')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data.items }
    }

    return { success: false, error: response.msg || '获取游戏类型失败' }
  } catch (error) {
    console.error('Get game types error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}

/**
 * 认证方式开关配置（H5 登录页据此显示/隐藏登录与注册入口）
 */
export interface AuthConfig {
  username_register_enabled: boolean
  phone_register_enabled: boolean
  email_register_enabled: boolean
  google_login_enabled: boolean
  telegram_login_enabled: boolean
}

export async function getAuthConfig(): Promise<{
  success: boolean
  data?: AuthConfig
  error?: string
}> {
  try {
    const response = await get<AuthConfig>('/api/v1/public/auth-config')

    if (response.code === 0 && response.data) {
      return { success: true, data: response.data }
    }

    return { success: false, error: response.msg || '获取认证配置失败' }
  } catch (error) {
    console.error('Get auth config error:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '网络错误',
    }
  }
}
